import React from 'react';
import { Link } from 'react-router-dom';
import { Container, Row, Col, Card } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBookOpen, faArrowRight } from '@fortawesome/free-solid-svg-icons';

// Define blog posts and content pages that can be recommended
const recommendedContent = [
  { 
    path: '/blog', 
    title: 'Read Our Latest Insights on Academic Success', 
    description: 'Tips, strategies, and guidance for achieving academic excellence.',
    type: 'blog'
  },
  { 
    path: '/how-it-works', 
    title: 'How Our Academic Assistance Works',
    description: 'Learn about our process and how we help students achieve their academic goals.',
    type: 'informational'
  },
  { 
    path: '/faq', 
    title: 'Frequently Asked Questions',
    description: 'Find answers to common questions about our services.',
    type: 'informational'
  },
  { 
    path: '/testimonials', 
    title: 'Success Stories from Our Students',
    description: 'Read what our clients say about our academic assistance services.',
    type: 'testimonials'
  },
  { 
    path: '/refund-guarantee', 
    title: 'Our Refund Guarantee Policy',
    description: 'Learn about our commitment to your satisfaction with our money-back guarantee.',
    type: 'policy'
  },
  { 
    path: '/privacy-policy', 
    title: 'Privacy & Data Protection',
    description: 'How we protect your information and maintain confidentiality.',
    type: 'policy'
  },
  { 
    path: '/terms-conditions', 
    title: 'Terms of Service',
    description: 'Review our terms and conditions for using our academic services.',
    type: 'policy'
  },
  { 
    path: '/services', 
    title: 'Explore All Our Academic Services',
    description: 'Discover the full range of academic assistance we provide.',
    type: 'services'
  }
];

interface ContentRecommendationsProps {
  currentPath: string;
  count?: number;
  title?: string;
  subtitle?: string;
  preferredTypes?: string[];
}

/**
 * ContentRecommendations component to add outgoing links to each page
 * This improves SEO by ensuring pages are not dead-ends
 */
const ContentRecommendations: React.FC<ContentRecommendationsProps> = ({
  currentPath,
  count = 2,
  title = "Recommended Resources",
  subtitle = "Explore more helpful content from our academic experts",
  preferredTypes = []
}) => {
  // First filter out the current page
  let filteredContent = recommendedContent.filter(content => content.path !== currentPath);
  
  // If preferred types are specified, prioritize them
  if (preferredTypes.length > 0) {
    const priorityContent = filteredContent.filter(content => preferredTypes.includes(content.type));
    const otherContent = filteredContent.filter(content => !preferredTypes.includes(content.type));
    
    // Randomize both arrays
    priorityContent.sort(() => 0.5 - Math.random());
    otherContent.sort(() => 0.5 - Math.random());
    
    // Combine with priority content first
    filteredContent = [...priorityContent, ...otherContent];
  } else {
    // Otherwise just randomize all content
    filteredContent.sort(() => 0.5 - Math.random());
  }
  
  // Select the number of recommendations needed
  const selectedContent = filteredContent.slice(0, count);

  return (
    <section className="content-recommendations py-4">
      <Container>
        <div className="text-center mb-4">
          <h3 className="mb-2">{title}</h3>
          <p className="text-muted">{subtitle}</p>
        </div>
        
        <Row>
          {selectedContent.map((content, index) => (
            <Col md={6} key={index} className="mb-3">
              <Card className="h-100 recommendation-card">
                <Card.Body>
                  <div className="d-flex align-items-start">
                    <div className="recommendation-icon me-3">
                      <FontAwesomeIcon icon={faBookOpen} />
                    </div>
                    <div>
                      <Card.Title>{content.title}</Card.Title>
                      <Card.Text>{content.description}</Card.Text>
                      <Link to={content.path} className="recommendation-link">
                        Read More <FontAwesomeIcon icon={faArrowRight} size="sm" />
                      </Link>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>
      </Container>
    </section>
  );
};

export default ContentRecommendations; 